// ─────────────────────────────────────────────────────────────────────────────
// settingsService.js — Enterprise settings API calls
// ─────────────────────────────────────────────────────────────────────────────
import api from "./api";

/**
 * GET /settings/company
 * Returns CompanyEntity:
 *   id, workspaceName, adminEmail (read-only), primaryContactEmail,
 *   industry?, companySize?, auditLogRetentionDays?, createdAt, updatedAt
 */
export async function getCompanySettings() {
  return api.get("/settings/company");
}

/**
 * PUT /settings/company
 * Admin only. Updatable fields only — adminEmail is NOT updatable.
 * @param {{ workspaceName?, primaryContactEmail?, industry?, companySize?, auditLogRetentionDays? }} data
 */
export async function updateCompanySettings(data) {
  return api.put("/settings/company", data);
}

/**
 * GET /settings/admin-roles
 * Returns the finalized role definitions derived from the backend RBAC config
 * (config/rbac.ts) — the single source of truth the API also enforces.
 * Display only — no save endpoint exists.
 *
 * Response also includes `modules` (ordered module keys) and `levels` (ascending
 * access-level ranking) so the grid can be rendered without hardcoding either.
 *
 * Shape: { roles: Array<{
 *   role: string,
 *   description: string,
 *   permissions: {
 *     user_management: 'none'|'read'|'read_write'|'full_access',
 *     policy_management: 'none'|'read'|'read_write'|'full_access',
 *     node_monitoring: 'none'|'read'|'read_write'|'full_access',
 *     analytics: 'none'|'read'|'read_write'|'full_access',
 *     settings: 'none'|'read'|'read_write'|'full_access',
 *   }
 * }> }
 */
export async function getAdminRoles() {
  return api.get("/settings/admin-roles");
}

/**
 * GET /settings/notifications
 * Workspace-wide alert-category toggles. Returns:
 *   { alerts: { criticalSystem, securityThreats, compliance, systemUpdates } }
 * These gate which notification categories the backend generates/delivers.
 */
export async function getNotificationSettings() {
  return api.get("/settings/notifications");
}

/**
 * PUT /settings/notifications
 * Admin only (settings:read_write). Partial patch of the alert toggles.
 * @param {{ criticalSystem?, securityThreats?, compliance?, systemUpdates? }} alerts
 * @returns {{ alerts }} the full new preference set
 */
export async function updateNotificationSettings(alerts) {
  return api.put("/settings/notifications", alerts);
}

// ── Integrations ──────────────────────────────────────────────────────────────

/**
 * GET /settings/integrations
 * Connection status per integration (Slack only today). Never returns the
 * webhook URL — status only.
 * Shape: { integrations: [{ type: "slack", connected: boolean }] }
 */
export async function getIntegrations() {
  return api.get("/settings/integrations");
}

/**
 * PUT /settings/integrations/slack — connect/update the Slack webhook.
 * Admin only. The URL must be an https://hooks.slack.com/services/… webhook.
 */
export async function connectSlack(webhookUrl) {
  return api.put("/settings/integrations/slack", { webhookUrl });
}

/** DELETE /settings/integrations/slack — disconnect. Admin only. */
export async function disconnectSlack() {
  return api.delete("/settings/integrations/slack");
}

/** POST /settings/integrations/slack/test — send a test message. Admin only. */
export async function testSlack() {
  return api.post("/settings/integrations/slack/test");
}

// ── Alert-preference metadata (labels/descriptions for the Notifications tab) ──
export const ALERT_PREFS = [
  { key: "criticalSystem",  label: "Critical System Alerts", sub: "Node failures, major network disruptions" },
  { key: "securityThreats", label: "Security Threats",       sub: "Blocked attacks, policy violations" },
  { key: "compliance",      label: "Compliance Summaries",   sub: "Weekly audit and regulatory reports" },
  { key: "systemUpdates",   label: "System Updates",         sub: "Maintenance windows, platform upgrades" },
];

// ── CompanySize options (must match backend enum exactly) ─────────────────────
export const COMPANY_SIZES = [
  "1 - 49",
  "50 - 99",
  "100 - 499",
  "500 - 999",
  "1,000 - 5,000",
  "5,000+",
];

export const INDUSTRIES = [
  "Technology", "Finance", "Healthcare", "Government",
  "Education", "Retail", "Manufacturing", "Other",
];

// ── Permission display helpers ────────────────────────────────────────────────
export const PERMISSION_MODULES = [
  { key: "user_management",   label: "User Mgmt"   },
  { key: "policy_management", label: "Policy Mgmt" },
  { key: "node_monitoring",   label: "Nodes"        },
  { key: "analytics",         label: "Analytics"    },
  { key: "settings",          label: "Settings"     },
];

export const ACCESS_LEVEL_CONFIG = {
  none:         { label: "None",       color: "#3a5548" },
  read:         { label: "Read",       color: "#ffc542" },
  read_write:   { label: "Read/Write", color: "#00c8e8" },
  full_access:  { label: "Full",       color: "#00e87a" },
};

export const ROLE_COLORS = {
  "Admin":          "#00e87a",
  "Security Admin": "#a855f7",
  "IT Manager":     "#00c8e8",
  "Auditor":        "#ffc542",
  "User":           "#6a9080",
};
